'use client';

import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { GamePanel } from '../ui/GamePanel';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { SeatData } from '@/components/grid';
import type { V2GridRewardEvent, V2GridSeatEvent } from '@/lib/types/v2';
import {
  RewardsTab,
  SeatSalesChart,
  ListingsChart,
  EventFeed,
  type TimeRange,
} from '@/components/grid-v2';
import type { LobbyGridPage } from '@/hooks/use-lobby-grid-page';
import { useLobbyStatsPanelData } from '@/features/lobby/hooks/use-lobby-stats-panel-data';

type TabKey = 'rewards' | 'sales' | 'listings';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'rewards', label: 'Rewards' },
  { key: 'sales', label: 'Sales' },
  { key: 'listings', label: 'Listings' },
];

/** Copy shown in the disabled-rewards-tab tooltip. Boards without yield
 *  adapters can never accrue rewards, so the tab is dead — we keep the
 *  tab in the rail (rather than hiding it) so the visual layout is
 *  stable across boards. */
const NO_YIELD_TOOLTIP = 'This board does not generate yield';

/** Feed filters mirror the canonical event taxonomy used elsewhere in
 *  the lobby — sales (state-changing trades + forfeits) and listings
 *  (initial claims + price updates). Kept colocated so a tweak here
 *  doesn't have to chase its peer in `use-lobby-stats-panel-data.ts`. */
const SALE_EVENT_FEED_TYPES = ['BOUGHT_FROM_CREATOR', 'BOUGHT_OUT', 'FORFEITED'] as const;
const LISTING_EVENT_FEED_TYPES = ['BOUGHT_VACANT', 'PRICE_UPDATED'] as const;

export interface RewardsPanelProps {
  boardId: string;
  boardName: string;
  /** Single `useLobbyGridPage` instance — identical API payload to the grid page. */
  gridPage: LobbyGridPage;
  /** Mock / non-resolved boards: same shapes as `useLobbyRewards` for offline demos. */
  fallbackRewards: {
    rewardEvents: V2GridRewardEvent[];
    saleEvents: V2GridSeatEvent[];
    listingsSeats: SeatData[];
  };
  /** True when the board id maps to a live onchain grid (not a static mock).
   *  Combined with `gridPage.gridLoading` to decide whether to render the
   *  skeleton — live boards never show the mock fallback charts, they show a
   *  skeleton until `stats.isLive` flips. */
  isLive?: boolean;
  /** External lobby-wide "board is switching" signal (from
   *  `isBoardDataLoading` in `LobbyClient`). When true the panel forces
   *  the skeleton even if `stats.isLive` hasn't flipped yet — same signal
   *  the other side panels receive so the whole lobby HUD loads as a unit. */
  loading?: boolean;
  /** True when the board has at least one yield adapter wired up (or the
   *  adapter list is still loading, so we can't yet rule yield out).
   *  False means the board can never accrue rewards — the panel disables
   *  the Rewards tab and defaults to Sales instead. Optional, defaults
   *  to `true` so existing callers keep their previous behaviour. */
  hasYieldAdapters?: boolean;
  priceDecimals: number;
  priceTokenPriceUsd: number;
  priceTokenSymbol?: string;
  className?: string;
}

/**
 * Activity HUD: same charts, feeds, filters, and copy as the single-grid page
 * (`Rewards` / `Tile Sales` / `Listings` tabs), wrapped in lobby `GamePanel` chrome.
 */
export function RewardsPanel({
  boardId,
  boardName,
  gridPage,
  fallbackRewards,
  isLive = false,
  loading = false,
  hasYieldAdapters = true,
  priceDecimals,
  priceTokenPriceUsd,
  priceTokenSymbol,
  className,
}: RewardsPanelProps) {
  // Yield-less boards land on Sales (the most useful surface when
  // there's nothing to claim) instead of an empty Rewards tab. The
  // initialiser only runs once per mount, so subsequent transitions
  // (creator adding/removing the board's first adapter mid-session)
  // are handled by the effect below.
  const [tab, setTab] = useState<TabKey>(() => (hasYieldAdapters ? 'rewards' : 'sales'));
  // If the current tab becomes unreachable (creator removes the last
  // adapter while the user is staring at the Rewards tab) bounce them
  // to Sales so they're not stuck on a tab whose button is now
  // disabled. The reverse direction (yield gets added back) deliberately
  // does *not* auto-switch — the user picked Sales and we respect that.
  useEffect(() => {
    if (!hasYieldAdapters && tab === 'rewards') {
      setTab('sales');
    }
  }, [hasYieldAdapters, tab]);
  const stats = useLobbyStatsPanelData(boardId, gridPage);
  // Live boards render a skeleton while the main grid query is in flight —
  // the stats panel reads from `gridPage`, so `stats.isLive` doesn't flip
  // until the grid payload lands. Without this gate we'd briefly flash the
  // mock-data branch below on a real board. The explicit `loading` flag
  // from `LobbyClient` ORs in so the skeleton also appears during any
  // coordinated board-switch transition, not just the live-data ramp.
  const showSkeleton = loading || (isLive && !stats.isLive);

  const rangeToSince = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    return (r: TimeRange) => {
      if (r === '24h') return now - 86_400;
      if (r === '7d') return now - 7 * 86_400;
      if (r === '1m') return now - 30 * 86_400;
      return 0;
    };
  }, []);

  const shellClass = '[&_.text-muted-foreground]:text-zinc-500 [&_.border-border\\/50]:border-amber-500/15';

  return (
    <GamePanel
      title="Activity"
      className={cn('w-[min(280px,calc(100vw-24px))]', className)}
      bodyClassName="p-2 pt-1.5 space-y-2"
      key={boardName}
    >
      <div
        role="tablist"
        aria-label="Board activity"
        className={cn(
          'flex gap-0.5 p-0.5 rounded-[3px] bg-black/40 border border-amber-500/15',
          showSkeleton && 'opacity-60',
        )}
      >
        {TABS.map(({ key, label }) => {
          const isActive = tab === key;
          // Only the Rewards tab is gated on yield availability;
          // Sales / Listings stay reachable on every board because
          // they reflect tile economics rather than yield accrual.
          const isYieldGated = key === 'rewards' && !hasYieldAdapters;
          const isDisabled = showSkeleton || isYieldGated;
          const button = (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-disabled={isYieldGated || undefined}
              onClick={() => {
                if (isDisabled) return;
                setTab(key);
              }}
              disabled={isDisabled}
              className={cn(
                'flex-1 h-6 text-[10px] font-mono uppercase tracking-[0.15em] rounded-[2px] transition-colors',
                isActive
                  ? 'bg-amber-500/15 text-amber-100 shadow-[inset_0_0_0_1px_rgba(255,215,120,0.25)]'
                  : 'text-zinc-400 hover:text-zinc-100 hover:bg-white/5',
                showSkeleton && 'cursor-not-allowed hover:bg-transparent hover:text-zinc-400',
                isYieldGated && 'cursor-not-allowed text-zinc-600 hover:bg-transparent hover:text-zinc-600',
              )}
            >
              {label}
            </button>
          );
          if (!isYieldGated) return button;
          // Disabled `<button>` elements don't dispatch pointer events
          // in every browser, which would suppress Radix's hover
          // detection. Wrapping the trigger in a span gives Radix a
          // reliable hover target while the button itself stays
          // visually-disabled and unclickable. `tabIndex={0}` keeps
          // the tooltip reachable via keyboard focus.
          return (
            <Tooltip key={key}>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="flex-1 inline-flex">
                  {button}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-[10px] font-mono uppercase tracking-[0.15em]">
                {NO_YIELD_TOOLTIP}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      {showSkeleton ? (
        <RewardsPanelSkeleton tab={tab} />
      ) : stats.isLive ? (
        <div className={cn('space-y-1', shellClass)}>
          {tab === 'rewards' && (
            <RewardsTab
              rewardEvents={stats.rewardEventsUsd}
              gridId={stats.gridId}
              timeframe={stats.rewardsTimeframe}
              onTimeframeChange={stats.setRewardsTimeframe}
              isUsdMode
              coinImageUrl={stats.coinImageUrl}
              pendingYield={gridPage.pendingYieldDisplay}
              chartBuckets={stats.chartBucketsData ?? undefined}
              chartLoading={stats.chartLoading}
            />
          )}
          {tab === 'sales' && (
            <>
              <SeatSalesChart
                snapshots={stats.floorPriceSnapshots}
                saleAnnotations={stats.saleAnnotations}
                range={stats.salesRange}
                onRangeChange={stats.setSalesRange}
                taxToken={stats.taxToken}
                taxTokenDecimalsIndexer={stats.taxTokenDecimalsIndexer}
                taxTokenDecimals={stats.taxTokenDecimals}
                takeoverPriceUsd={stats.takeoverPriceUsd}
              />
              <div className="border-t border-amber-500/15 mt-3 pt-2">
                <p className="text-[10px] text-zinc-500 font-medium px-1 mb-1">Buyouts & Forfeits</p>
                <EventFeed
                  events={stats.allV2Events}
                  filterTypes={[...SALE_EVENT_FEED_TYPES]}
                  sinceTimestamp={stats.chartRangeToSince(stats.salesRange)}
                  taxToken={stats.taxToken}
                  taxTokenDecimals={stats.taxTokenDecimals}
                  taxTokenDecimalsIndexer={stats.taxTokenDecimalsIndexer}
                  takeoverPriceUsd={stats.takeoverPriceUsd}
                />
              </div>
            </>
          )}
          {tab === 'listings' && (
            <>
              <ListingsChart
                seats={fallbackRewards.listingsSeats}
                taxTokenDecimals={stats.taxTokenDecimals}
                tokenPriceUsd={priceTokenPriceUsd}
                taxTokenSymbol={priceTokenSymbol}
              />
              <div className="border-t border-amber-500/15 mt-3 pt-2">
                <p className="text-[10px] text-zinc-500 font-medium px-1 mb-1">Price Changes</p>
                <EventFeed
                  events={stats.allV2Events}
                  filterTypes={[...LISTING_EVENT_FEED_TYPES]}
                  sinceTimestamp={0}
                  taxToken={stats.taxToken}
                  taxTokenDecimals={stats.taxTokenDecimals}
                  taxTokenDecimalsIndexer={stats.taxTokenDecimalsIndexer}
                  takeoverPriceUsd={stats.takeoverPriceUsd}
                />
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={cn('space-y-1', shellClass)}>
          {tab === 'rewards' && (
            <RewardsTab
              rewardEvents={fallbackRewards.rewardEvents}
              gridId={boardId}
              isUsdMode
              coinImageUrl={null}
              pendingYield={0}
              chartBuckets={undefined}
              chartLoading={false}
            />
          )}
          {tab === 'sales' && (
            <>
              <SeatSalesChart snapshots={[]} saleAnnotations={[]} />
              <div className="border-t border-amber-500/15 mt-3 pt-2">
                <p className="text-[10px] text-zinc-500 font-medium px-1 mb-1">Buyouts & Forfeits</p>
                <EventFeed
                  events={fallbackRewards.saleEvents}
                  filterTypes={[...SALE_EVENT_FEED_TYPES]}
                  sinceTimestamp={rangeToSince('7d')}
                  taxTokenDecimals={priceDecimals}
                  taxTokenDecimalsIndexer={priceDecimals}
                  takeoverPriceUsd={priceTokenPriceUsd}
                />
              </div>
            </>
          )}
          {tab === 'listings' && (
            <>
              <ListingsChart
                seats={fallbackRewards.listingsSeats}
                taxTokenDecimals={priceDecimals}
                tokenPriceUsd={priceTokenPriceUsd}
                taxTokenSymbol={priceTokenSymbol}
              />
              <div className="border-t border-amber-500/15 mt-3 pt-2">
                <p className="text-[10px] text-zinc-500 font-medium px-1 mb-1">Price Changes</p>
                <EventFeed
                  events={fallbackRewards.saleEvents}
                  filterTypes={[...LISTING_EVENT_FEED_TYPES]}
                  sinceTimestamp={0}
                  taxTokenDecimals={priceDecimals}
                  taxTokenDecimalsIndexer={priceDecimals}
                  takeoverPriceUsd={priceTokenPriceUsd}
                />
              </div>
            </>
          )}
        </div>
      )}

    </GamePanel>
  );
}

/**
 * Skeleton placeholder rendered while the live grid payload is in flight on a
 * live board. Mirrors the three tab layouts (rewards KPI card + chart, sales
 * chart + feed, listings chart + feed) at roughly their eventual sizes so the
 * panel body doesn't reflow when `stats.isLive` flips to true.
 */
function RewardsPanelSkeleton({ tab }: { tab: TabKey }) {
  return (
    <div
      className="space-y-2"
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading activity"
    >
      {tab === 'rewards' ? (
        <>
          <div className="flex gap-1.5">
            <div className="flex-1 h-12 rounded-[3px] bg-white/[0.04] border border-white/5 animate-pulse" />
            <div className="flex-1 h-12 rounded-[3px] bg-white/[0.04] border border-white/5 animate-pulse" />
          </div>
          <div className="h-[120px] rounded-[3px] bg-white/[0.03] border border-white/5 animate-pulse" />
        </>
      ) : (
        <>
          <div className="h-[96px] rounded-[3px] bg-white/[0.03] border border-white/5 animate-pulse" />
          <div className="pt-2 border-t border-amber-500/15 space-y-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-[28px] rounded-[3px] bg-white/[0.03] animate-pulse"
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
